import { FormEvent, useEffect, useState } from 'react';
import { TextBlock } from '../../Atoms/Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { McpAccessProfileInput, McpAccessScope } from '../../../store/McpAccess';
import { McpAccessFields } from './McpAccessFields';

interface McpAccessCreateFormProps {
  onCreate: (input: McpAccessProfileInput) => Promise<boolean> | boolean | void;
  allowedScopes: McpAccessScope[];
  submitting?: boolean;
}

export const McpAccessCreateForm = ({ onCreate, allowedScopes, submitting = false }: McpAccessCreateFormProps) => {
  const defaultScope = allowedScopes[0];
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState<McpAccessScope>(defaultScope);

  // A role change can shrink the allowed scopes out from under a selection made under the old role;
  // drop any now-disallowed scope so the form can never submit (or display) an elevated scope.
  useEffect(() => {
    setScope((current) => (allowedScopes.includes(current) ? current : allowedScopes[0]));
  }, [allowedScopes]);

  const canSubmit = title.trim().length > 0 && !submitting;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    const created = await onCreate({ title: title.trim(), scope });
    if (created) {
      setTitle('');
      setScope(defaultScope);
    }
  };

  return (
    <form
      data-testid="mcp-access-create-form"
      className="space-y-3 max-w-md"
      onSubmit={handleSubmit}>
      <TextBlock
        weight="semibold"
        size="base"
        color="grey9">
        Create a new token
      </TextBlock>
      <McpAccessFields
        title={title}
        scope={scope}
        allowedScopes={allowedScopes}
        onTitleChange={setTitle}
        onScopeChange={setScope}
      />
      <button
        type="submit"
        disabled={!canSubmit}
        className={DEFAULT_ADVANCE_BUTTON_STYLE}>
        Create token
      </button>
    </form>
  );
};
